import { findRecordsByModelName } from './findRecordsByModelName'

export type YhcMasterVersionPeriod = {
  id: string
  enabledFrom: string
  disabledAt: string
}

/**
 * datepicker は Date を返すため、文字列以外も受け付けて比較用文字列へ正規化する。
 */
const toDateOrDatetimeString = (
  dateOrDatetime: string | Date | null | undefined,
): string => {
  if (dateOrDatetime == null || dateOrDatetime === '') return ''
  if (typeof dateOrDatetime === 'string') {
    return dateOrDatetime.trim()
  }
  if (dateOrDatetime instanceof Date && !Number.isNaN(dateOrDatetime.getTime())) {
    const y = dateOrDatetime.getFullYear()
    const m = String(dateOrDatetime.getMonth() + 1).padStart(2, '0')
    const d = String(dateOrDatetime.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  return String(dateOrDatetime).trim()
}

/**
 * マスタバージョン検索用に日付を DATETIME 比較できる形へ正規化する。
 * `YYYY-MM-DD` および `YYYY-MM-DD HH:mm:ss` は当日 00:00:00 にする。
 */
export const toVersionLookupDatetime = (
  dateOrDatetime: string | Date | null | undefined,
): string => {
  const trimmed = toDateOrDatetimeString(dateOrDatetime)
  if (!trimmed) return ''
  const day = trimmed.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return trimmed
  }
  return `${day} 00:00:00`
}

export const laterYmd = (
  a: string | null | undefined,
  b: string | null | undefined,
): string => {
  const da = (a || '').slice(0, 10)
  const db = (b || '').slice(0, 10)
  if (!da) return db
  if (!db) return da
  return da >= db ? da : db
}

export const earlierYmd = (
  a: string | null | undefined,
  b: string | null | undefined,
): string => {
  const da = (a || '').slice(0, 10)
  const db = (b || '').slice(0, 10)
  if (!da) return db
  if (!db) return da
  return da <= db ? da : db
}

/**
 * サービスレベル検索の表示中ランクのうち、一番早い日付を返す。
 * 見積りフォームでは使わない（そちらは表示中お届け日）。
 */
export const pickEarliestVisibleServiceLevelDate = (
  eDate: string | null | undefined,
  fgDate: string | null | undefined,
  remoteDate: string | null | undefined,
  options: { includeE: boolean; includeFg: boolean },
): string => {
  let picked = ''
  if (options.includeE) {
    picked = earlierYmd(picked, eDate)
    picked = earlierYmd(picked, remoteDate)
  }
  if (options.includeFg) {
    picked = earlierYmd(picked, fgDate)
  }
  return picked
}

export const addDaysYmd = (ymd: string, days: number): string => {
  const day = (ymd || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return ''
  const d = new Date(`${day}T00:00:00`)
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const date = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${date}`
}

/**
 * 料金・日時シミュレータの画面表示お届け日。versionDelivery の基準にする。
 */
export const pickEstimateFormDeliveryDateForVersion = (input: {
  pickupDate: string | null | undefined
  isSameDayPickupAndDelivery: boolean
  isRemoteIsland: boolean
  remoteIslandDeliveryDateAdjusted: string | null | undefined
  displayedDeliveryDate: string | null | undefined
}): string | null => {
  const pickup = (input.pickupDate || '').slice(0, 10) || null
  if (input.isSameDayPickupAndDelivery && pickup) return pickup
  if (input.isRemoteIsland) {
    const remote = (input.remoteIslandDeliveryDateAdjusted || '').slice(0, 10)
    if (remote) return remote
    return pickup ? addDaysYmd(pickup, 14) || pickup : pickup
  }
  const displayed = (input.displayedDeliveryDate || '').slice(0, 10)
  return displayed || pickup
}

/**
 * 指定された日時に基づいて、バージョンを返す
 * 期間が重なる場合は enabledFrom が新しい方を採用する
 */
export const fetchVersionBasedOnDatetime = async (
  baseDatetime: string | Date | null | undefined,
): Promise<string | undefined> => {
  const lookup = toVersionLookupDatetime(baseDatetime)
  if (!lookup) return undefined
  return (
    await findRecordsByModelName('yhc_master_versions', {
      fields: ['id'],
      filter: {
        enabledFrom: { _lte: lookup },
        disabledAt: { _gte: lookup },
      },
      sort: ['-enabledFrom'],
      limit: 1,
    })
  )?.[0]?.id
}

export const fetchMasterVersionPeriods = async (): Promise<YhcMasterVersionPeriod[]> => {
  return (
    (await findRecordsByModelName('yhc_master_versions', {
      fields: ['id', 'enabledFrom', 'disabledAt'],
      sort: ['enabledFrom'],
      limit: -1,
    })) || []
  )
}
